// src/components/ReserveLayout.jsx
import React, { useRef } from "react";
import {
  Box,
  Drawer,
  Container,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import BuscadorFechas from "./BuscadorFechas";
import ListaHabitaciones from "./ListaHabitaciones";
import ListaHabitacionesMobile from "./ListaHabitacionesMobile";
import Overlay from "./Overlay";
import ResumenCotizacion from "./ResumenCotizacion";
import CardFacturacion from "./CardFacturation";

import { useReserve } from "../context/ReserveContext";

import image1 from "../images/imagen1.webp";
import image2 from "../images/imagen2.webp";
import image3 from "../images/imagen3.webp";
import image4 from "../images/imagen4.webp";
import image5 from "../images/imagen5.webp";

const ReserveLayout = () => {
  const {
    propertyId,
    startDate,
    endDate,
    rooms,
    occupants,
    setOccupants,
    numGuests,
    pricesRules,
    loading,
    mobileOpen,
    setMobileOpen,
    isOverlayVisible,
    setOverlayVisible,
    currency,
    mpPublicKey,
    mpLocale,
  } = useReserve();

  const targetRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const totalNights = (() => {
    if (!startDate || !endDate) return 0;
    const a = new Date(startDate);
    const b = new Date(endDate);
    const diff = (b - a) / (1000 * 60 * 60 * 24);
    return Number.isFinite(diff) ? Math.max(diff, 1) : 0;
  })();

  const handleDrawerToggle = () => {
    const hasSelection = Object.values(occupants || {}).some(
      (v) => (v ?? 0) > 0,
    );

    if (hasSelection) {
      setMobileOpen(!mobileOpen);
      return;
    }

    setOverlayVisible(true);
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const calculateOffer = (room) => {
    if (!startDate || !endDate) return 0;
    const nights = totalNights || 1;

    if (room?.type === "shared") {
      const pricePerNight = Number(room?.pricePerGuest || 0);
      return pricePerNight * nights * Number(numGuests || 1);
    }

    if (room?.type === "private") {
      const factor = pricesRules?.[numGuests] || pricesRules?.[5] || 1;
      const adjusted = Number(room?.pricePerGuest || 0) * Number(factor || 1);
      return adjusted * nights;
    }

    return 0;
  };

  const formatCurrency = (value) => {
    const cur = (currency || "ARS").toUpperCase();
    const locale = cur === "CLP" ? "es-CL" : "es-AR";
    return (value ?? 0).toLocaleString(locale, {
      style: "currency",
      currency: cur,
      minimumFractionDigits: 0,
    });
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        overflow: "hidden",
        backgroundColor: "#0B1630",
      }}
    >
      {/* =======================
          FONDO PRO (collage visible + overlay pro)
         ======================= */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 0,

          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "1.2fr 0.8fr" },
          gridTemplateRows: { xs: "repeat(3, 140px)", md: "repeat(3, 1fr)" },
          gap: { xs: "10px", md: "14px" },
          p: { xs: 1.2, md: 2.2 },

          // ✅ menos “mancha”, más foto
          transform: "scale(1.04)",
          filter: "blur(1.2px) saturate(1.05) contrast(1.02) brightness(1.02)",
          opacity: 0.9,

          gridTemplateAreas: {
            xs: `"a a"
                 "b c"
                 "d e"`,
            md: `"a b"
                 "a c"
                 "d e"`,
          },
        }}
      >
        <Box
          sx={{
            gridArea: "a",
            borderRadius: 3,
            backgroundImage: `url(${image1})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            boxShadow: "0 18px 50px rgba(0,0,0,0.35)",
          }}
        />
        <Box
          sx={{
            gridArea: "b",
            borderRadius: 3,
            backgroundImage: `url(${image2})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            boxShadow: "0 18px 50px rgba(0,0,0,0.35)",
          }}
        />
        <Box
          sx={{
            gridArea: "c",
            borderRadius: 3,
            backgroundImage: `url(${image3})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            boxShadow: "0 18px 50px rgba(0,0,0,0.35)",
          }}
        />
        <Box
          sx={{
            gridArea: "d",
            borderRadius: 3,
            backgroundImage: `url(${image4})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            boxShadow: "0 18px 50px rgba(0,0,0,0.35)",
          }}
        />
        <Box
          sx={{
            gridArea: "e",
            borderRadius: 3,
            backgroundImage: `url(${image5})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            boxShadow: "0 18px 50px rgba(0,0,0,0.35)",
          }}
        />
      </Box>

      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          // ✅ oscurece bordes, deja ver el centro
          background: `
            radial-gradient(900px 520px at 50% 18%, rgba(11,22,48,0.25) 0%, rgba(11,22,48,0.5) 55%, rgba(11,22,48,0.60) 100%),
            linear-gradient(180deg, rgba(11,22,48,0.40) 0%, rgba(11,22,48,0.50) 55%, rgba(11,22,48,0.6) 100%)
          `,
        }}
      />

      {/* =======================
          CONTENIDO
         ======================= */}
      <Box sx={{ position: "relative", zIndex: 2 }}>
        <Drawer
          anchor="bottom"
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
        >
          <ResumenCotizacion
            rooms={rooms}
            occupants={occupants}
            totalNights={totalNights}
            handleDrawerToggle={handleDrawerToggle}
            formatCurrency={formatCurrency}
          />
        </Drawer>

        <BuscadorFechas />

        {endDate ? (
          <Container
            maxWidth="xl"
            sx={{
              mt: 3,
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
              minHeight: "60vh",
              pt: "12vh",
            }}
          >
            {loading ? (
              <Box sx={{ width: "100%", opacity: 0.95 }}>
                <Skeleton
                  variant="rectangular"
                  animation="pulse"
                  height="100vh"
                  width="100%"
                  sx={{
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.14)",
                    backdropFilter: "blur(6px)",
                  }}
                />
              </Box>
            ) : (
              <>
                <Box sx={{ flex: 2, overflowY: "auto" }}>
                  {isMobile ? (
                    <ListaHabitacionesMobile
                      rooms={rooms}
                      numGuests={numGuests}
                      totalNights={totalNights}
                      formatCurrency={formatCurrency}
                      calculateOffer={calculateOffer}
                      startDate={startDate}
                      endDate={endDate}
                      currency={currency}
                      propertyId={propertyId}
                      mpPublicKey={mpPublicKey}
                      mpLocale={mpLocale}
                      occupants={occupants}
                      setOccupants={setOccupants}
                      onOpenResumen={handleDrawerToggle}
                    />
                  ) : (
                    <ListaHabitaciones
                      rooms={rooms}
                      numGuests={numGuests}
                      totalNights={totalNights}
                      formatCurrency={formatCurrency}
                      calculateOffer={calculateOffer}
                      startDate={startDate}
                      endDate={endDate}
                      currency={currency}
                      propertyId={propertyId}
                      mpPublicKey={mpPublicKey}
                      mpLocale={mpLocale}
                      occupants={occupants}
                      setOccupants={setOccupants}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    display: {
                      xs: "none",
                      sm: "none",
                      md: "none",
                      lg: "block",
                      xl: "block",
                    },
                  }}
                >
                  <Box
                    sx={{
                      padding: 3,
                      borderRadius: 0,
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    }}
                  >
                    <CardFacturacion
                      habitacionesSeleccionadas={rooms
                        .filter((room) => (occupants?.[room.roomId] ?? 0) > 0)
                        .map((room) => ({
                          name: room.name,
                          type: room.type,
                          occupants: occupants?.[room.roomId] ?? 0,
                          pricePerNight: room.pricePerGuest,
                          totalPrice:
                            Number(room.pricePerGuest || 0) *
                            (occupants?.[room.roomId] ?? 0) *
                            (totalNights || 0),
                          currency,
                        }))}
                      startDate={startDate}
                      endDate={endDate}
                    />
                  </Box>
                </Box>
              </>
            )}
          </Container>
        ) : (
          <Container
            maxWidth="xl"
            sx={{ paddingTop: "14vh", paddingBottom: 6 }}
          >
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.14)",
                border: "1px solid rgba(255,255,255,0.14)",
                backdropFilter: "blur(10px)",
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: 800, color: "rgba(255,255,255,0.92)" }}
              >
                Elegí fechas para ver disponibilidad
              </Typography>
              <Typography
                sx={{ mt: 1, opacity: 0.85, color: "rgba(255,255,255,0.78)" }}
              >
                Seleccioná fecha de inicio y fin de tu estadía, y la cantidad de
                huéspedes, todo en el selector de arriba.
              </Typography>
            </Box>
          </Container>
        )}

        <Overlay
          isVisible={isOverlayVisible}
          setIsVisible={setOverlayVisible}
          targetRef={targetRef}
          onClose={() => setOverlayVisible(false)}
        />
      </Box>
    </Box>
  );
};

export default ReserveLayout;
